import React, { createContext, useState, useContext, useCallback, useEffect } from "react";
import { fetchInstructorEntitlements } from "./Instructor/services/instructorEntitlementsApi";
import {
  cachePackageLimits,
  planLimitsFromEntitlements,
} from "./Instructor/services/instructorMeterLimits";
import {
  fetchOrganizations,
  setActiveOrganization,
} from "./Instructor/services/orgSwitcherApi";
import {
  isImpersonating,
  exitImpersonationView,
} from "./Instructor/impersonationView";

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateBody, setGateBody] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(localStorage.getItem("orgId") || null);

  const loadEntitlements = useCallback(async (nextUserId, orgId) => {
    if (!nextUserId) {
      setEntitlements(null);
      setEntitlementsLoading(false);
      return null;
    }
    setEntitlementsLoading(true);
    // Default to the org selected in the switcher so meters/limits always reflect the
    // active organization — even on refresh or callers that don't pass an orgId. The
    // backend ignores an orgId the user isn't an active member of, so a stale value
    // safely falls back to their server-side default org.
    const scopedOrgId = orgId || localStorage.getItem("orgId") || undefined;
    try {
      const data = await fetchInstructorEntitlements(nextUserId, scopedOrgId);
      setEntitlements(data);
      if (data?.role) {
        localStorage.setItem("instructorRole", data.role);
      }
      if (data?.tier) {
        localStorage.setItem("studyak_plan_tier", data.tier);
      }
      if (data?.orgId) {
        localStorage.setItem("orgId", data.orgId);
      }
      if (data?.meters) {
        cachePackageLimits(planLimitsFromEntitlements(data));
      }
      return data;
    } catch (error) {
      console.error("Failed to load instructor entitlements:", error);
      // Never downgrade known-good entitlements on a transient failure (network,
      // CORS, cert, 5xx). A refresh that briefly fails must not make an
      // organization admin look like a starter user. Keep whatever we already
      // have; only when we have nothing, fall back to the last cached tier/role
      // from localStorage rather than fabricating "starter".
      setEntitlements((prev) => {
        if (prev) return prev;
        const cachedTier = localStorage.getItem("studyak_plan_tier");
        const cachedRole = localStorage.getItem("instructorRole");
        const cachedOrgId = localStorage.getItem("orgId");
        return {
          tier: cachedTier || "starter",
          role: cachedRole || null,
          orgId: cachedOrgId || null,
          meters: {},
          features: {},
          ui: {},
        };
      });
      return null;
    } finally {
      setEntitlementsLoading(false);
    }
  }, []);

  const clearEntitlements = useCallback(() => {
    setEntitlements(null);
    localStorage.removeItem("instructorRole");
  }, []);

  const loadOrgs = useCallback(async (uid) => {
    if (!uid) {
      setOrgs([]);
      return [];
    }
    try {
      const list = await fetchOrganizations(uid);
      setOrgs(list || []);
      return list;
    } catch (error) {
      console.error("Failed to load organizations:", error);
      setOrgs([]);
      return [];
    }
  }, []);

  const switchOrg = useCallback(
    async (uid, orgId) => {
      if (isImpersonating()) {
        // Exit view first — the admin re-triggers the switch from their own org.
        exitImpersonationView();
        return;
      }
      await setActiveOrganization(uid, orgId);
      localStorage.setItem("orgId", orgId);
      setCurrentOrg(orgId);
      await loadEntitlements(uid, orgId);
    },
    [loadEntitlements],
  );

  const openUpgradeGate = useCallback((body) => {
    setGateBody(body || { message: "Upgrade required" });
    setGateOpen(true);
  }, []);

  const closeUpgradeGate = useCallback(() => {
    setGateOpen(false);
    setGateBody(null);
  }, []);

  // Load entitlements once when the provider mounts (i.e. on every page refresh)
  // so the real tier/role/features are always restored, independent of which
  // route mounted or whether a per-page loader fired. Without this, a refresh
  // could leave `entitlements` null and the UI would show the "starter" default.
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      loadEntitlements(storedUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContext.Provider
      value={{
        userId,
        setUserId,
        packageDetails,
        setPackageDetails,
        entitlements,
        setEntitlements,
        entitlementsLoading,
        loadEntitlements,
        clearEntitlements,
        gateOpen,
        gateBody,
        openUpgradeGate,
        closeUpgradeGate,
        orgs,
        currentOrg,
        loadOrgs,
        switchOrg,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
